/**
 * WhatsApp share (feature #7) — TEXT ONLY (image cards retired by request).
 * Builds a formatted message from the sheet's own columns and opens wa.me.
 * No contact footer, no prices. Logs a 'shared_product' activity.
 *
 * Field order, hidden fields and blank-line separators come from the admin's
 * per-tab share layout (app_config/displayFormat, see buildSpecShareLines in
 * @mitang/shared). Without one, legacy behavior: attrs order with a blank line
 * before the UPI row.
 */
import type { Product, TabDisplayFormat } from '@mitang/shared';
import { buildSpecShareLines } from '@mitang/shared';
import { formatPriceCell } from '../../lib/format';
import { logActivity } from '../../lib/activity/activityLogger';

/** Price columns, passed in only for users the admin allow-listed. */
export type PriceAttrs = Array<{ k: string; v: string }> | null | undefined;

/** The sheet's columns as label/value pairs (legacy fields as fallback). */
function shareRows(p: Product): Array<[string, string]> {
  if (p.attrs && p.attrs.length > 0) {
    return p.attrs
      .filter((a) => a.v !== p.modelNumber)
      .map((a) => [a.k, a.v] as [string, string]);
  }
  const legacy: Array<[string, string | undefined]> = [
    ['Part No', p.partNo],
    ['Processor', p.processor],
    ['RAM', p.ram],
    ['Storage', p.storage],
    ['Graphics', p.graphics],
    ['Display', p.display],
    ['OS', p.os],
  ];
  return legacy.filter((r): r is [string, string] => !!r[1]);
}

export function buildShareText(
  p: Product,
  priceAttrs?: PriceAttrs,
  fmt?: TabDisplayFormat | null,
): string {
  const lines: string[] = [`*${p.modelNumber}*`, ...buildSpecShareLines(shareRows(p), fmt)];
  // Price is included ONLY when the sharer can see prices (they're fetched from
  // the restricted collection and passed in by the screen).
  if (priceAttrs?.length) {
    lines.push('');
    for (const a of priceAttrs) lines.push(`*${a.k}: ${formatPriceCell(a.v)}*`);
  }
  return lines.join('\n');
}

export function buildWhatsAppUrl(
  p: Product,
  priceAttrs?: PriceAttrs,
  fmt?: TabDisplayFormat | null,
): string {
  return `https://wa.me/?text=${encodeURIComponent(buildShareText(p, priceAttrs, fmt))}`;
}

/** Open the WhatsApp share sheet (text message) and log the share event. */
export function shareProductOnWhatsApp(
  p: Product,
  priceAttrs?: PriceAttrs,
  fmt?: TabDisplayFormat | null,
): void {
  logActivity('shared_product', {
    productId: p.id,
    modelNumber: p.modelNumber,
    category: p.category,
  });
  window.open(buildWhatsAppUrl(p, priceAttrs, fmt), '_blank', 'noopener,noreferrer');
}
